import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";

const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Temporary", "Internship"];
const SAFETY_LEVELS = ["Entry", "OSHA 10", "OSHA 30", "Certified Safety Professional", "Manager / Director"];

export default function PostJob() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [states, setStates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: "", job_title: "", state: "", city: "", employment_type: "Full-time",
    salary_range: "", salary_not_disclosed: false, description: "", min_qualifications: "",
    hiring_email: "", company_website: "", billing_contact: "", remote: false, multi_state: false, safety_level: "",
  });

  useEffect(() => { api.get("/states").then(({ data }) => setStates(data)); }, []);
  useEffect(() => {
    if (!loading && !user) navigate("/login", { state: { redirect: "/post-job" } });
    if (!loading && user && user.role === "applicant") toast.error("Switch to an employer account to post jobs.");
    if (user) setForm((f) => ({ ...f, hiring_email: f.hiring_email || user.email }));
  }, [user, loading, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [aiOpen, setAiOpen] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const genDescription = async () => {
    if (!aiNotes.trim()) return toast.error("Add a few notes first");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/job-description", {
        notes: aiNotes, job_title: form.job_title, company: form.company_name, state: form.state,
      });
      set("description", data.text);
      setAiOpen(false);
      toast.success("Description drafted — review and edit as needed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed"); }
    finally { setAiLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (user?.role === "applicant") return toast.error("Employer account required.");
    if (!form.remote && !form.state) return toast.error("Select a state or mark the role remote.");
    setSubmitting(true);
    try {
      const payload = { ...form, state: form.remote && !form.state ? "Remote" : form.state, safety_level: form.safety_level || null };
      const { data: job } = await api.post("/jobs", payload);
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "employer_job_post", origin_url: window.location.origin, purpose: "job_post", job_id: job.job_id,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-10 flex-1 w-full">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-amber-600 mb-2"><ShieldCheck className="h-4 w-4" /> Employer Listing</div>
        <h1 className="font-display text-3xl font-extrabold text-navy-800">Post a safety job</h1>
        <p className="text-slate-500 mt-2">$99 for a 60-day listing. Publishes instantly after payment.</p>

        <form onSubmit={submit} className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company name"><Input data-testid="job-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} required /></Field>
            <Field label="Job title"><Input data-testid="job-title" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} required /></Field>
          </div>

          <div className="flex flex-wrap gap-6 py-2">
            <label className="flex items-center gap-2 text-sm font-medium text-navy-800"><Switch data-testid="job-remote" checked={form.remote} onCheckedChange={(v) => set("remote", v)} /> Remote role</label>
            <label className="flex items-center gap-2 text-sm font-medium text-navy-800"><Switch data-testid="job-multistate" checked={form.multi_state} onCheckedChange={(v) => set("multi_state", v)} /> Multi-state</label>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={form.remote ? "State (optional for remote)" : "State"}>
              <Select value={form.state} onValueChange={(v) => set("state", v)}>
                <SelectTrigger data-testid="job-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-64">{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="City"><Input data-testid="job-city" value={form.city} onChange={(e) => set("city", e.target.value)} required={!form.remote} /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Employment type">
              <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                <SelectTrigger data-testid="job-emptype"><SelectValue /></SelectTrigger>
                <SelectContent>{EMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Safety level (optional)">
              <Select value={form.safety_level} onValueChange={(v) => set("safety_level", v)}>
                <SelectTrigger data-testid="job-safety"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>{SAFETY_LEVELS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-navy-800 mb-2"><Switch data-testid="job-salary-hidden" checked={form.salary_not_disclosed} onCheckedChange={(v) => set("salary_not_disclosed", v)} /> Salary not disclosed</label>
            {!form.salary_not_disclosed && <Field label="Salary range"><Input data-testid="job-salary" value={form.salary_range} onChange={(e) => set("salary_range", e.target.value)} placeholder="$70,000 – $95,000" /></Field>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-navy-800">Job description</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setAiOpen(true)} data-testid="jd-ai-open" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"><Sparkles className="h-3.5 w-3.5" /> Draft with AI</Button>
            </div>
            <Textarea data-testid="job-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={6} required />
          </div>
          <Field label="Minimum qualifications"><Textarea data-testid="job-qualifications" value={form.min_qualifications} onChange={(e) => set("min_qualifications", e.target.value)} rows={4} required /></Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Hiring email (receives applications)"><Input data-testid="job-hiring-email" type="email" value={form.hiring_email} onChange={(e) => set("hiring_email", e.target.value)} required /></Field>
            <Field label="Company website (optional)"><Input data-testid="job-website" value={form.company_website} onChange={(e) => set("company_website", e.target.value)} placeholder="company.com" /></Field>
          </div>
          <Field label="Billing contact (optional)"><Input data-testid="job-billing" value={form.billing_contact} onChange={(e) => set("billing_contact", e.target.value)} /></Field>

          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <div className="text-sm text-slate-500 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Publishes instantly after payment</div>
            <Button data-testid="employer-checkout-button" type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-navy-900 font-bold h-11 px-6">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pay $99 & publish"}
            </Button>
          </div>
        </form>
      </div>
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent data-testid="jd-ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Draft your job description</DialogTitle>
            <DialogDescription>Add a few notes and AI will write a polished description. You can edit it after.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="jd-ai-notes" value={aiNotes} onChange={(e) => setAiNotes(e.target.value)} rows={5} placeholder="e.g. Night-shift safety officer, oversee PPE compliance, OSHA 30 required, incident reporting…" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>Cancel</Button>
            <Button data-testid="jd-ai-generate" onClick={genDescription} disabled={aiLoading} className="bg-navy-800 hover:bg-navy-900 text-white gap-2">{aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-navy-800">{label}</Label><div className="mt-1.5">{children}</div></div>
);
